/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SearchBar } from '../../../components/Header/SearchBar';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { isNavigationExpanded } from '../../../state/app';
import { useGetBingoPageDataLazyQuery } from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-bingo-page-data.query.generated';
import { useGetSpinsPageDataLazyQuery } from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-spins-page-data.query.generated';
import {
  useGetBingoCarouselLazyQuery,
  type GetBingoCarouselQuery,
} from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-bingo-carousel.query.generated';

import {
  useGetSpinsCarouselLazyQuery,
  type GetSpinsCarouselQuery,
} from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-spins-carousel.query.generated';
import { Spinner } from '../../../components/shared/Spinner';
import { locale, xTenantId } from '../../../consts';
import { Endpoint } from '../../../models/api.model';
import { ItvRowCollection } from '../../../components/Itv/ItvRowCollection';
import { BingoFilter, BingoFilterValue } from '../../../components/Itv/ItvRowCollection/components/BingoFilter';
import { ItvCarousel } from '../../../components/Itv/ItvCarousel';
import { ItvCarouselSlide } from '../../../components/Itv/ItvCarousel/ItvCarouselSlide';
import { getImageUrl } from '../../../utils/navigation';
import { RowCollectionLayout } from '../../../components/Itv/ItvRowCollection/layouts/types';
import { buildCollectionTiles } from '../../../components/Itv/ItvRowCollection/utils/collectionTiles';
import { useGetItvCollectionBySlugLazyQuery } from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/getItvCollectionBySlug.query.generated';
import { ItvViewAll } from '../../../components/Itv/ItvViewAll';
import { ItvCollectionType } from '../../../components/Itv/types';
import { useGameDetailsUrl } from '../../../hooks/games';
import { GameType } from '../../../components/Games/games.model';
import { useAuth } from 'react-oidc-context';
import { useSignIn } from '../../../hooks/auth';
import { sortBy, uniq } from 'lodash';
import { ParsedPromo } from '../../../components/Layout/Promotions/types';
import { JackPots } from '../../../components/Games/service';
import './ItvGenericPage.scss';
import { MediaBreakpoint, useBreakpointInfo } from '../../../hooks/useDeviceInfo';
import { usePromoClaim } from '../../../hooks/usePromoClaim';
import {
  TagsObjectQuery,
  useTagsObjectLazyQuery,
} from '../../../libs/graphql/baseAppAPI/queries/__generated__/tags-get.query.generated';
import { useUserContext } from '../../../providers/UserProvider';
import { useFetchRecentlyPlayedGames } from '../../../hooks/useFetchRecentlyPlayedGames';
import { GameItemResponse } from '../../../components/Games/games.model';
import { SEO } from '../../../components/seo/SEO';
import { InnerHTML } from '../../../components/shared/InnerHTML';
import { ContentPreview } from '../../../components/shared/ContentPreview';
import {
  BingoGameSnapshotSubscription,
  useBingoGameSnapshotSubscription,
} from '../../../libs/graphql/baseAppAPI/subscriptions/__generated__/bingo-data-udapted.generated';
import { useOnJackpotUpdatedSubscription } from '../../../libs/graphql/betzoneDirectusAPI/subscriptions/__generated__/jackpot-updated.generated';
import { parseJackpotXML } from '../../../utils/parseJackpotXML';
import { useGetJackpotsQuery } from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/getJackpots.query.generated';
import { ItvTopCarousel } from '../../../components/Itv/ItvTopCarousel';
import { useFetchPlatformPromos } from '../../../hooks/useFetchPlatformPromos';
import { PlatformProduct } from '../../../components/Itv/ItvShop/types';
import { getXPShopProducts } from '../../../components/Itv/ItvShop/service';
import { getActualXPToken } from '../../../utils/auth';
import { useSnackbar } from '../../../hooks/snackbar';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import { storageClaimedProductId } from '../../../utils/storage';
import { trackEvent } from '../../../tracking/tracking';

export type HandleClaimClick = (promo?: ParsedPromo) => void;
type CarouselPageData = GetBingoCarouselQuery['pageData'][number] | GetSpinsCarouselQuery['pageData'][number];

type CarouselSlideNode = NonNullable<NonNullable<CarouselPageData['carouselData']>['slides']>[number];

const createCarouselSlides = (
  isAuthenticated: boolean,
  slides?: Array<CarouselSlideNode | null> | null,
  tagsResponse?: TagsObjectQuery | null
): ItvCarouselSlide[] => {
  if (!slides?.length) return [];

  const filteredSlidesByTagId = slides.filter((e) => {
    const tagId = e?.slideData?.tag_id;

    // if slide does`nt have tag show it anyway
    if (!tagId) return true;

    // check if user has matched tagId with slide tag id
    const hasMatchedTag = tagsResponse?.tagsObject?.some((e) => e.tag?.id === tagId);

    return hasMatchedTag;
  });

  return filteredSlidesByTagId.reduce<ItvCarouselSlide[]>((acc, slideNode, index) => {
    const slideData = slideNode?.slideData;

    if (!slideData) return acc;

    if (isAuthenticated && slideNode.slideData?.hide_for_logged_in) return acc;

    const [translation] = slideData.translations ?? [];
    const titleDesktop = translation?.title_desktop || translation?.title;
    const subtitleDesktop = translation?.subtitle_desktop || translation?.subtitle;

    const titleMobile = translation?.title_mobile || titleDesktop;
    const subtitleMobile = translation?.subtitle_mobile || subtitleDesktop;

    const desktopImage = getImageUrl(slideData.desktop_image?.filename_disk);
    const mobileImage = getImageUrl(slideData.mobile_image?.filename_disk);
    const imgSrc = desktopImage || mobileImage;

    if (!titleDesktop || !imgSrc) {
      return acc;
    }

    acc.push({
      id: `${titleDesktop}-${index}`,
      title: titleDesktop || '',
      subTitle: subtitleDesktop || '',
      titleDesktop: titleDesktop || '',
      subtitleDesktop: subtitleDesktop || '',
      titleMobile: titleMobile || '',
      subtitleMobile: subtitleMobile || '',
      imgSrc,
      mobileImgSrc: mobileImage || undefined,
      buttonLabel: translation?.cta_text ?? undefined,
      buttonLink: translation?.cta_link ?? undefined,
      openInNewTab: translation?.cta_open_in_new_tab ?? undefined,
      termsAndConditions: translation?.key_terms ?? undefined,
    });

    return acc;
  }, []);
};

export const ItvGenericPage: FC<{
  variant: 'bingo' | 'spins' | 'prizes';
  subpage?: string;
}> = ({ variant, subpage: subpageProp }) => {
  const { t } = useTranslations();
  const { subpage: subpageParam } = useParams();
  const subpage = subpageProp || subpageParam;
  const navigate = useNavigate();
  const location = useLocation();
  const platformPromos = useFetchPlatformPromos();
  const { XPToken, setXPToken, userProfileId } = useUserContext();
  const { addSnack } = useSnackbar();
  const navigationExpanded = useSelector(isNavigationExpanded);

  const [bingoFilters, setBingoFilters] = useState<Record<string, BingoFilterValue>>({});
  const [platformProducts, setPlatformProducts] = useState<PlatformProduct[]>([]);
  const [platformProductsLoading, setPlatformProductsLoading] = useState<boolean>(false);

  const [bingoRealTimeData, setBingoRealTimeData] = useState<Record<string, BingoGameSnapshotSubscription | null>>();

  const [fetchUserTags, { data: tagsResponse }] = useTagsObjectLazyQuery({
    variables: {
      xTenantId,
      externalResource: 'UserProfileId',
      externalResourceId: userProfileId,
    },
    fetchPolicy: 'network-only',
    context: { endpoint: Endpoint.appAPI },
  });

  const [fetchBingoPageData, { data: bingoData, loading: bingLoading }] = useGetBingoPageDataLazyQuery();
  const [fetchSpinsPageData, { data: spinsData, loading: spinsLoading }] = useGetSpinsPageDataLazyQuery();
  const [fetchBingoCarouselData, { data: bingoCarouselData, loading: bingoCarouselLoading }] =
    useGetBingoCarouselLazyQuery();
  const [fetchSpinsCarouselData, { data: spinsCarouselData, loading: spinsCarouselLoading }] =
    useGetSpinsCarouselLazyQuery();
  const [fetchCollectionBySlug, { data: collectionData }] = useGetItvCollectionBySlugLazyQuery();

  const [viewAllCollection, setViewAllCollection] = useState<string | null>(null);
  const [handleClaimClick, setHandleClaimClick] = useState<HandleClaimClick>(() => () => {});
  const [jackPots, setJackPots] = useState<JackPots>({});

  const isBellow1200 = useBreakpointInfo([MediaBreakpoint.BELOW_1200]);
  const isMobileDevice = useBreakpointInfo([MediaBreakpoint.BELOW_DESKTOP]);
  const { isAuthenticated, user } = useAuth();
  const signIn = useSignIn();
  const accessToken = user?.access_token || '';

  const { data: recentlyPlayedGamesData, fetchData: fetchRecentlyPlayedGames } = useFetchRecentlyPlayedGames({
    userId: userProfileId,
    accessToken,
  });

  usePromoClaim({ setHandleClaimClick });

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchUserTags();

    if (XPToken) {
      setPlatformProductsLoading(true);
      (async (): Promise<void> => {
        try {
          const activeToken = await getActualXPToken(XPToken, setXPToken);
          const XPProducts = await getXPShopProducts(activeToken);

          setPlatformProducts(XPProducts);
        } finally {
          setPlatformProductsLoading(false);
        }
      })();
    }
  }, [isAuthenticated, XPToken]);

  const data = bingoData || spinsData;

  const [pageData] = data?.pageData || [];
  const loading =
    bingLoading ||
    spinsLoading ||
    bingoCarouselLoading ||
    spinsCarouselLoading ||
    platformProductsLoading ||
    !data ||
    platformPromos.isPromosLoading;
  const isGameOnPage = pageData?.collections?.some((collection) => collection?.Collections_id?.games?.length);
  const isBingosOnPage = pageData?.collections?.some((collection) => collection?.Collections_id?.bingos?.length);
  const isProductsOnPage = pageData?.collections?.some(
    (collection) => collection?.Collections_id?.star_products?.length
  );
  const isRecentlyPlayedGamesOnPage = pageData?.collections?.some(
    (collection) => collection?.Collections_id?.type === ItvCollectionType.RECENTLY_PLAYED_GAMES
  );

  const { data: jackPotData } = useOnJackpotUpdatedSubscription({ skip: !isGameOnPage });
  const { data: jackpotInitData } = useGetJackpotsQuery({
    skip: !isGameOnPage,
    context: { endpoint: Endpoint.betzoneDirectusAPI },
  });

  useBingoGameSnapshotSubscription({
    skip: !isBingosOnPage,
    variables: {
      tenantId: xTenantId,
    },
    onData: ({ data }) => {
      const gameId = data.data?.bingoGameSnapshot?.gameId;

      if (!gameId) return;

      setBingoRealTimeData((prevValue) => {
        return { ...prevValue, [gameId]: data.data } as Record<string, BingoGameSnapshotSubscription | null>;
      });
    },
  });

  useEffect(() => {
    if (isAuthenticated && isRecentlyPlayedGamesOnPage && userProfileId && accessToken) {
      fetchRecentlyPlayedGames();
    }
  }, [isAuthenticated, isRecentlyPlayedGamesOnPage, userProfileId, accessToken, fetchRecentlyPlayedGames]);

  const carouselData = bingoCarouselData || spinsCarouselData;
  const [carouselPageData] = carouselData?.pageData || [];

  const heroSlides = createCarouselSlides(isAuthenticated, carouselPageData?.carouselData?.slides, tagsResponse);

  const slidesToRender = heroSlides.length ? heroSlides : [];
  const { buildGameDetailsUrl } = useGameDetailsUrl(variant as 'bingo' | 'spins', GameType.Bingo);

  const handleViewAll = (collectionType: ItvCollectionType, bingoViewAllSlug: string, collectionSlug: string): void => {
    if (collectionType === ItvCollectionType.BINGO) {
      const url = buildGameDetailsUrl(bingoViewAllSlug, 'real');

      if (isAuthenticated) {
        navigate(url);
      } else {
        signIn({ redirectUri: new URL(url, window.location.origin).toString() });
      }
    } else {
      const opts = {
        variables: { language: locale, slug: collectionSlug, limit: 2500 },
        context: { endpoint: Endpoint.betzoneDirectusAPI },
      };

      fetchCollectionBySlug(opts);
      setViewAllCollection(collectionSlug);

      const urlParams = new URLSearchParams(location.search);

      urlParams.set('viewAll', collectionSlug);
      navigate(`${location.pathname}?${urlParams.toString()}`, { replace: true });
      window.scrollTo(0, 0);
    }
  };

  const handleBackToMain = (): void => {
    setViewAllCollection(null);
    const urlParams = new URLSearchParams(location.search);

    urlParams.delete('viewAll');
    const newSearch = urlParams.toString();

    navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
    window.scrollTo(0, 0);
  };

  const handleBingoFilterChange = (collectionId: string, value: BingoFilterValue): void => {
    setBingoFilters((prev) => ({
      ...prev,
      [collectionId]: value,
    }));
  };

  useEffect(() => {
    if (!subpage) return;

    const urlParams = new URLSearchParams(location.search);
    const viewAll = urlParams.get('viewAll');

    if (viewAll) {
      setViewAllCollection(viewAll);
      // Fetch the collection data for view all
      const viewAllOpts = {
        variables: { language: locale, slug: viewAll, limit: 2500 },
        context: { endpoint: Endpoint.betzoneDirectusAPI },
      };

      fetchCollectionBySlug(viewAllOpts);
    } else {
      // Clear view all state when no viewAll parameter is present
      setViewAllCollection(null);
    }

    const nowIso = new Date().toISOString();
    const [nowDate, nowTimeWithMs] = nowIso.split('T');
    const [nowTime] = nowTimeWithMs.split('.');

    const pageOpts = {
      variables: {
        language: locale,
        pageSlug: subpage,
        limit: 100,
      },
      context: { endpoint: Endpoint.betzoneDirectusAPI },
    };

    const carouselOpts = {
      variables: {
        language: locale,
        pageSlug: subpage,
        nowDate,
        nowTime,
      },
      context: { endpoint: Endpoint.betzoneDirectusAPI },
      fetchPolicy: 'no-cache' as const,
    };

    if (variant === 'bingo') {
      fetchBingoPageData(pageOpts);
      fetchBingoCarouselData(carouselOpts);
    }

    if (variant === 'spins') {
      fetchSpinsPageData(pageOpts);
      fetchSpinsCarouselData(carouselOpts);
    }
  }, [subpage, location.search]);

  useEffect(() => {
    if (jackPotData || jackpotInitData) {
      const jPDataSource = jackPotData
        ? jackPotData?.jackpot_ticker_mutated?.data?.data
        : jackpotInitData?.jackpot_ticker?.data;
      const parsedJackpots = parseJackpotXML(String(jPDataSource));

      parsedJackpots && setJackPots(parsedJackpots);
    }
  }, [jackPotData, jackpotInitData]);

  useEffect(() => {
    isProductsOnPage && trackEvent({ event: 'shop_view' });
  }, [isProductsOnPage]);

  useEffect(() => {
    // Check whether saved product ID from logged out Shop claiming process is existed into platform response list and if it's not remove a storage value
    // and show an error to an user
    if (platformProducts.length) {
      const storedClaimedProductId = storageClaimedProductId.get();

      if (storedClaimedProductId) {
        const isClaimedProductExisted = platformProducts.find((product) => product.name === storedClaimedProductId);

        if (!isClaimedProductExisted) {
          storageClaimedProductId.remove();

          addSnack({
            type: 'error',
            message: t('shop.snackbar.not-found'),
            theme: 'dark',
          });
        }
      }
    }
  }, [platformProducts]);

  return (
    <div className={`itv-generic-page ${navigationExpanded ? 'itv-generic-page--nav-expanded' : ''}`}>
      {loading ? (
        <Spinner />
      ) : (
        <>
          <SEO title={pageData.SEO_title} description={pageData.SEO_description}></SEO>
          <ItvCarousel slides={slidesToRender} autoplayInterval={10000} showDots={!isMobileDevice}></ItvCarousel>
          <div className="itv-generic-page__content">
            <div className="itv-generic-page__search">
              <SearchBar isUsedOverlay={true} isShort />
            </div>
            {viewAllCollection && collectionData?.collection?.[0] ? (
              <ItvViewAll
                collection={collectionData.collection[0]}
                onBack={handleBackToMain}
                platformPromos={platformPromos}
                platformProducts={platformProducts}
                handleClaimClick={handleClaimClick}
              />
            ) : (
              pageData?.collections?.map((collection) => {
                const layoutType = collection?.Collections_id?.layout_type as RowCollectionLayout | undefined;
                const title = collection?.Collections_id?.translations?.[0]?.title;
                const backgroundEnabled = collection?.Collections_id?.background_enabled ?? false;
                const backgroundImage = collection?.Collections_id?.background_image?.filename_disk;
                const backgroundImageMob = collection?.Collections_id?.background_image_mob?.filename_disk;

                const collectionId = collection?.id || '';
                const selectedFilter = bingoFilters[collectionId] || 'ALL';

                // Get all available bingo options
                const allBingoOptions = sortBy(
                  uniq(collection?.Collections_id?.bingos?.map((e) => e?.bingos_id?.number_of_balls)).filter(
                    (e) => e != null
                  )
                )
                  .reverse()
                  .map(String);

                const options = ['ALL', ...allBingoOptions];

                // Filter bingo games based on selected filter
                let filteredBingos = collection?.Collections_id?.bingos || [];

                if (selectedFilter !== 'ALL') {
                  filteredBingos =
                    filteredBingos?.filter((bingo) => String(bingo?.bingos_id?.number_of_balls) === selectedFilter) ||
                    [];
                }

                const recentlyPlayedGames =
                  collection?.Collections_id?.type === ItvCollectionType.RECENTLY_PLAYED_GAMES
                    ? (recentlyPlayedGamesData?.games as GameItemResponse[] | undefined)
                    : undefined;

                const tiles = buildCollectionTiles({
                  collectionId: collection?.id,
                  collectionType: collection?.Collections_id?.type,
                  layoutType,
                  games: collection?.Collections_id?.games,
                  bingos: filteredBingos,
                  promotions: collection?.Collections_id?.promotions,
                  products: collection?.Collections_id?.star_products,
                  platformProducts,
                  recentlyPlayedGames,
                  fallbackImages: [],
                  stubImageClassName: 'itv-generic-page__demo-image',
                  platformPromos,
                  handleClaimClick,
                  jackPots,
                  bingoRealTimeData,
                  isMobile: isBellow1200,
                  isAuthenticated,
                });

                const headerAction =
                  collection?.Collections_id?.type === 'bingo' ? (
                    <BingoFilter
                      options={options}
                      initial={'ALL'}
                      onChange={(value): void => handleBingoFilterChange(collectionId, value)}
                    />
                  ) : null;

                const isRecentlyPlayedGamesCollection =
                  collection?.Collections_id?.type === ItvCollectionType.RECENTLY_PLAYED_GAMES;

                // Skip recently played games collection when user is not authenticated or has no games
                if (isRecentlyPlayedGamesCollection && (!isAuthenticated || !recentlyPlayedGamesData?.games?.length)) {
                  return null;
                }

                return (
                  Boolean(tiles.length) &&
                  (layoutType === 'top-games' ? (
                    <ItvTopCarousel key={collectionId} games={collection?.Collections_id?.games} title={title} />
                  ) : (
                    <ItvRowCollection
                      key={collection?.id}
                      //@ts-ignore
                      layout_type={layoutType}
                      //@ts-ignore
                      type={collection?.Collections_id?.type}
                      //@ts-ignore
                      title={title}
                      headerAction={headerAction}
                      showViewAll={!isRecentlyPlayedGamesCollection}
                      backgroundEnabled={backgroundEnabled}
                      backgroundImage={backgroundImage}
                      backgroundImageMob={backgroundImageMob}
                      onViewAll={(): void =>
                        handleViewAll(
                          collection?.Collections_id?.type as ItvCollectionType,
                          collection?.Collections_id?.view_all_bingo?.translations?.[0]?.slug || '',
                          collection?.Collections_id?.translations?.[0]?.slug || ''
                        )
                      }
                    >
                      {tiles}
                    </ItvRowCollection>
                  ))
                );
              })
            )}
            <div className="itv-generic-page__seo-content">
              {pageData.SEO_content && (
                <ContentPreview>
                  <InnerHTML text={pageData.SEO_content as string} />
                </ContentPreview>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
